/**
 * Pure purchase logic (CC-6: NEVER use Lingui macros here).
 *
 * A purchase order (purchase_order) follows the flow:
 *   draft → confirmed → received
 *
 * On receipt, a receipt (stock_picking type 'receipt') is generated that increases
 * on-hand. The receipt may be PARTIAL: each line accumulates `received_qty`; if
 * received < ordered, a BACKORDER remains (the rest pending) and the order
 * stays 'confirmed'. When all lines are complete it moves to
 * 'received'.
 *
 * Quantities in WHOLE units (minor units, factor 1000), same as F1/F2.
 */

export type PoState = "draft" | "confirmed" | "received";

export const PO_STATES: readonly PoState[] = ["draft", "confirmed", "received"] as const;

export function isPoState(value: string): value is PoState {
  return (PO_STATES as readonly string[]).includes(value);
}

export function coercePoState(value: string | null | undefined): PoState {
  return value && isPoState(value) ? value : "draft";
}

/** Can it be confirmed? Only from draft. */
export function canConfirm(state: string | null | undefined): boolean {
  return coercePoState(state) === "draft";
}

/** Can it be received (in full or partially)? Only a confirmed order with pending quantities. */
export function canReceive(state: string | null | undefined): boolean {
  return coercePoState(state) === "confirmed";
}

/** Purchase-order line (the relevant subset for calculations). */
export interface PoLineLike {
  product_id: string | null;
  qty: number | null;
  price: number | null;
  received_qty: number | null;
}

/** Quantity still pending for a line (>= 0). */
export function pendingQty(line: PoLineLike): number {
  const qty = line.qty ?? 0;
  const received = line.received_qty ?? 0;
  return Math.max(qty - received, 0);
}

/** Are units still pending on any line? (open backorder) */
export function hasBackorder(lines: PoLineLike[]): boolean {
  return lines.some((l) => pendingQty(l) > 0);
}

/** Total ordered for the order (Σ qty), in minor units. */
export function totalOrdered(lines: PoLineLike[]): number {
  return lines.reduce((acc, l) => acc + (l.qty ?? 0), 0);
}

/** Total received for the order (Σ received_qty), in minor units. */
export function totalReceived(lines: PoLineLike[]): number {
  return lines.reduce((acc, l) => acc + (l.received_qty ?? 0), 0);
}

/**
 * Total order value (Σ qty × price), in minor units of money.
 * `qty` is scaled ×1000; `price` is per whole unit, so it is divided by
 * the factor to avoid carrying the quantity scale into the monetary value.
 */
export function totalValue(lines: PoLineLike[], qtyScale: number): number {
  return lines.reduce((acc, l) => acc + Math.round(((l.qty ?? 0) * (l.price ?? 0)) / qtyScale), 0);
}

/** A receipt allocation for a specific line (quantity to receive now). */
export interface ReceiptAllocation {
  productId: string;
  /** Quantity to receive in this receipt (minor units, > 0). */
  qty: number;
  /** Purchase cost per whole unit (for the AVCO valuation layer). */
  unitCost: number;
  /** Resulting accumulated `received_qty` after applying this receipt. */
  newReceivedQty: number;
}

/** Identified purchase-order line (with id) for planning the receipt. */
export interface IdentifiedPoLine extends PoLineLike {
  id: string;
}

/**
 * Plans a receipt from the quantities to receive per line. If
 * `requested` does not specify a line, it receives ALL pending for that line
 * (full receipt by default). It caps each quantity at the available pending
 * amount (you cannot receive more than is missing). It ignores lines with no pending quantity.
 *
 * @param lines      order lines (with id, qty, price, received_qty).
 * @param requested  map lineId → quantity to receive now (minor units).
 *                   If undefined, receive all pending for each line.
 */
export function planReceipt(
  lines: IdentifiedPoLine[],
  requested?: Map<string, number>,
): ReceiptAllocation[] {
  const allocations: ReceiptAllocation[] = [];
  for (const line of lines) {
    const productId = line.product_id;
    if (!productId) {
      continue;
    }
    const pending = pendingQty(line);
    if (pending <= 0) {
      continue;
    }
    const ask = requested ? (requested.get(line.id) ?? 0) : pending;
    const qty = Math.min(Math.max(ask, 0), pending);
    if (qty <= 0) {
      continue;
    }
    allocations.push({
      productId,
      qty,
      unitCost: line.price ?? 0,
      newReceivedQty: (line.received_qty ?? 0) + qty,
    });
  }
  return allocations;
}

/**
 * Resulting order state after a receipt: 'received' if no pending quantities
 * remain, 'confirmed' (open backorder) otherwise.
 *
 * @param linesAfter lines with their received_qty ALREADY updated.
 */
export function resolvePoStateAfterReceipt(linesAfter: PoLineLike[]): PoState {
  return hasBackorder(linesAfter) ? "confirmed" : "received";
}
